import React, { useState, useEffect } from 'react'
import {
  Segment,
  Header,
  Dropdown,
  Button,
  Icon,
  Label,
  Message,
  Divider,
  Progress,
  List,
  Transition,
  Accordion,
  Grid,
  Modal,
  Form,
  TextArea,
} from 'semantic-ui-react'
import { toast } from 'react-toastify'
import useAxios from '../lib/auth/useAxios'

const TakeoffActionPanel = ({
  websocket,
  takeoffId,
  documents,
  selectedDocument,
  onSendMessage,
  mode = 'plan',
  onModeChange,
  primaryColor,
  secondaryColor,
}) => {
  const axios = useAxios()
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [executing, setExecuting] = useState(false)
  const [todoList, setTodoList] = useState([])
  const [currentStep, setCurrentStep] = useState(null)
  const [executionLog, setExecutionLog] = useState([])
  const [showCustomPrompt, setShowCustomPrompt] = useState(false)
  const [customPrompt, setCustomPrompt] = useState('')

  // Fetch action templates on mount
  useEffect(() => {
    fetchActionTemplates()
  }, [])

  // Listen for action updates via WebSocket
  useEffect(() => {
    if (!websocket) return

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        handleActionUpdate(data)
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    }

    websocket.addEventListener('message', handleMessage)
    return () => websocket.removeEventListener('message', handleMessage)
  }, [websocket, todoList])

  const fetchActionTemplates = async () => {
    try {
      console.log('Fetching action templates...')
      const response = await axios.get('/main/action-templates/')
      console.log('Action templates response:', response.data)
      setTemplates(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching action templates:', error)
      console.error('Error details:', error.response?.data)
      toast.error('Failed to load action templates')
    }
  }

  const handleActionUpdate = (data) => {
    // Handle different types of action updates
    switch (data.type) {
      case 'agent_thinking':
        addToLog('thinking', data.message, data.details)
        break

      case 'agent_status':
        addToLog('status', data.message)
        break

      case 'plan_chunk':
        // Stream plan content to chat
        if (onSendMessage) {
          onSendMessage(data.content, 'assistant', true)
        }
        break

      case 'plan_complete':
        setTodoList(data.todo_list || [])
        addToLog('success', '✅ Plan created successfully', {
          total_steps: data.summary?.total_steps,
          estimated_duration: data.summary?.estimated_duration,
        })
        break

      case 'step_progress':
        updateTodoStatus(data.todo_id, data.status)
        setCurrentStep(data.todo_id)
        addToLog('progress', data.description, {
          step: data.step_number,
          tools: data.tools_required,
        })
        break

      case 'step_complete':
        updateTodoStatus(data.todo_id, 'completed')
        addToLog('success', data.message)
        break

      case 'step_failed':
        updateTodoStatus(data.todo_id, 'failed')
        addToLog('error', data.message, { error: data.error })
        break

      case 'tool_start':
        addToLog('tool', `🔧 ${data.message}`, { purpose: data.purpose })
        break

      case 'tool_result':
        addToLog(data.success ? 'success' : 'error', data.message, {
          summary: data.summary,
        })
        break

      case 'execution_complete':
        setExecuting(false)
        setCurrentStep(null)
        addToLog('complete', '🎉 Action completed!', {
          completed: data.completed_steps,
          total: data.total_steps,
          success_rate: `${data.success_rate?.toFixed(0)}%`,
        })
        break

      case 'action_error':
        setExecuting(false)
        addToLog('error', `❌ Error: ${data.error}`)
        toast.error(data.error)
        break
    }
  }

  const addToLog = (type, message, details = null) => {
    setExecutionLog((prev) => [
      ...prev,
      {
        type,
        message,
        details,
        timestamp: new Date().toISOString(),
      },
    ])
  }

  const updateTodoStatus = (todoId, status) => {
    setTodoList((prev) =>
      prev.map((todo) => (todo.id === todoId ? { ...todo, status } : todo))
    )
  }

  const executeAction = async () => {
    if (!selectedTemplate && !customPrompt) {
      toast.warning('Please select an action or enter a custom prompt')
      return
    }

    setExecuting(true)
    setExecutionLog([])
    setTodoList([])

    try {
      // Temporarily use chat action until yoda is fixed
      const actionMessage = {
        action: 'chat',
        data: {
          action_type: 'execute_template',
          template_id: selectedTemplate?.value,
          mode: mode,
          takeoff_id: takeoffId,
          prompt: customPrompt || undefined,
          message:
            customPrompt ||
            `Execute action template: ${selectedTemplate?.template?.name}`,
          context: {
            documents: documents?.map((doc) => ({
              id: doc.id,
              name: doc.name || doc.document_details?.document_name,
              type: doc.document_details?.document_extension,
              file: doc.document_details?.file,
            })),
            selected_document: selectedDocument
              ? {
                  id: selectedDocument.id,
                  name:
                    selectedDocument.name ||
                    selectedDocument.document_details?.document_name,
                  type: selectedDocument.document_details?.document_extension,
                  file: selectedDocument.document_details?.file,
                }
              : null,
            document_count: documents?.length || 0,
          },
        },
      }

      console.log('Sending action message:', actionMessage)
      websocket.send(JSON.stringify(actionMessage))

      // Add initial log entry
      addToLog('info', `Starting action in ${mode} mode...`)
    } catch (error) {
      console.error('Error executing action:', error)
      toast.error('Failed to start action: ' + error.message)
      setExecuting(false)
    }
  }

  const toggleMode = () => {
    const newMode = mode === 'plan' ? 'execute' : 'plan'
    if (onModeChange) {
      onModeChange(newMode)
    }
    // toast.info(`Switched to ${newMode} mode`)
  }

  const clearLog = () => {
    setExecutionLog([])
    setTodoList([])
  }

  const getLogIcon = (type) => {
    const icons = {
      thinking: 'lightbulb outline',
      status: 'info circle',
      progress: 'tasks',
      success: 'check circle',
      error: 'times circle',
      tool: 'wrench',
      complete: 'flag checkered',
      info: 'info',
    }
    return icons[type] || 'circle'
  }

  const getLogColor = (type) => {
    const colors = {
      thinking: 'blue',
      status: 'grey',
      progress: 'yellow',
      success: 'green',
      error: 'red',
      tool: 'purple',
      complete: 'green',
      info: 'blue',
    }
    return colors[type] || 'grey'
  }

  const templateOptions = templates.map((template) => ({
    key: template.id,
    value: template.id,
    text: template.name,
    content: (
      <div>
        <div className="text">{template.name}</div>
        <div className="description">{template.description}</div>
      </div>
    ),
    icon: template.icon || 'tasks',
  }))

  return (
    <Segment raised>
      {/* Mode Toggle */}
      <Grid>
        <Grid.Row>
          <Grid.Column width={8}>
            <Label
              color={mode === 'plan' ? 'blue' : 'green'}
              size="large"
              style={{ cursor: 'pointer' }}
              onClick={toggleMode}
            >
              <Icon name={mode === 'plan' ? 'clipboard outline' : 'play'} />
              {mode === 'plan' ? 'Plan Mode' : 'Execute Mode'}
              {/* <Label.Detail>Press Shift+Tab to toggle</Label.Detail> */}
            </Label>
          </Grid.Column>
          <Grid.Column width={8} textAlign="right">
            <Button size="mini" basic onClick={clearLog} disabled={executing}>
              <Icon name="trash" />
              Clear Log
            </Button>
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Action Selection */}
      <Segment secondary>
        <Form>
          <Form.Field className="action-panel-dropdown">
            <label>Select Action Template</label>
            <Dropdown
              placeholder="Choose an action..."
              fluid
              selection
              search
              options={templateOptions}
              value={selectedTemplate?.value}
              onChange={(e, { value }) => {
                const template = templates.find((t) => t.id === value)
                setSelectedTemplate({ value, template })
                setCustomPrompt('')
              }}
              disabled={executing}
            />
          </Form.Field>

          <Form.Field>
            <label>
              Or enter custom prompt
              <Label
                as="a"
                size="mini"
                pointing="left"
                onClick={() => setShowCustomPrompt(!showCustomPrompt)}
              >
                {showCustomPrompt ? 'Hide' : 'Show'}
              </Label>
            </label>
            <Transition visible={showCustomPrompt || !selectedTemplate}>
              <TextArea
                placeholder="Describe what you want to analyze..."
                value={customPrompt}
                onChange={(e, { value }) => {
                  setCustomPrompt(value)
                  if (value) setSelectedTemplate(null)
                }}
                disabled={executing}
                rows={3}
              />
            </Transition>
          </Form.Field>

          <Button
            fluid
            onClick={executeAction}
            disabled={executing || (!selectedTemplate && !customPrompt)}
            loading={executing}
            style={{
              backgroundColor: primaryColor || '#00b5ad',
              color: 'white',
              opacity:
                executing || (!selectedTemplate && !customPrompt) ? 0.5 : 1,
              cursor:
                executing || (!selectedTemplate && !customPrompt)
                  ? 'not-allowed'
                  : 'pointer',
            }}
          >
            <Icon name={mode === 'plan' ? 'clipboard check' : 'play'} />
            {executing
              ? 'Processing...'
              : `${mode === 'plan' ? 'Create Plan' : 'Execute Action'}`}
          </Button>
        </Form>
      </Segment>

      {/* Todo List */}
      {todoList.length > 0 && (
        <Segment>
          <Header as="h4">
            <Icon name="tasks" />
            Action Steps
          </Header>
          <List divided relaxed>
            {todoList.map((todo, index) => (
              <List.Item key={todo.id}>
                <List.Icon
                  name={
                    todo.status === 'completed'
                      ? 'check circle'
                      : todo.status === 'in_progress'
                      ? 'spinner'
                      : todo.status === 'failed'
                      ? 'times circle'
                      : 'circle outline'
                  }
                  color={
                    todo.status === 'completed'
                      ? 'green'
                      : todo.status === 'in_progress'
                      ? 'yellow'
                      : todo.status === 'failed'
                      ? 'red'
                      : 'grey'
                  }
                  loading={todo.status === 'in_progress'}
                />
                <List.Content>
                  <List.Header>
                    Step {index + 1}: {todo.description}
                  </List.Header>
                  {todo.tools && todo.tools.length > 0 && (
                    <List.Description>
                      Tools: {todo.tools.join(', ')}
                    </List.Description>
                  )}
                </List.Content>
              </List.Item>
            ))}
          </List>

          {/* Progress Bar */}
          {executing && (
            <Progress
              value={todoList.filter((t) => t.status === 'completed').length}
              total={todoList.length}
              progress="ratio"
              indicating
              size="small"
            />
          )}
        </Segment>
      )}

      {/* Execution Log */}
      {executionLog.length > 0 && (
        <Segment style={{ maxHeight: '300px', overflowY: 'auto' }}>
          <Header as="h4">
            <Icon name="list" />
            Execution Log
          </Header>
          <List>
            {executionLog.map((log, index) => (
              <List.Item key={index}>
                <List.Icon
                  name={getLogIcon(log.type)}
                  color={getLogColor(log.type)}
                />
                <List.Content>
                  <List.Description>
                    <small>
                      {new Date(log.timestamp).toLocaleTimeString()}
                    </small>
                  </List.Description>
                  <List.Header>{log.message}</List.Header>
                  {log.details && (
                    <List.Description>
                      <pre style={{ fontSize: '0.85em', margin: 0 }}>
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </List.Description>
                  )}
                </List.Content>
              </List.Item>
            ))}
          </List>
        </Segment>
      )}
    </Segment>
  )
}

export default TakeoffActionPanel
